/**
 * Panneau d'un sentier du réseau public (TrailOsm) touché sur la carte : sa fiche, son lien
 * avec le parc (associer ou retirer, même s'il est hors du contour), et la copie dans les
 * chemins de l'organisation, en entier ou en partie (découpe entre deux points).
 */
import { useTranslation } from 'react-i18next'
import type { TrailFeature } from '../../api'
import { formatLength } from '../../map/geo'

type Props = {
  feature: TrailFeature
  busy: boolean
  cutting: boolean
  cutPoints: number
  onBack: () => void
  onLink: () => void
  onUnlink: () => void
  onCopyWhole: () => void
  onStartCut: () => void
  onCancelCut: () => void
}

export function TrailPanel({ feature, busy, cutting, cutPoints, onBack, onLink, onUnlink, onCopyWhole, onStartCut, onCancelCut }: Props) {
  const { t } = useTranslation('gestion')
  const p = feature.properties
  return (
    <div className="g-editor__details">
      <button type="button" className="g-back" onClick={onBack}>
        <span className="mdi mdi-arrow-left" aria-hidden /> {t('carte.retour_liste')}
      </button>
      <div>
        <span className="g-badge g-badge--info">{t('carte.sentier_reseau')}</span>
        {p.custom && <span className="g-badge">{t('carte.personnalise')}</span>}
      </div>
      <h2 className="g-editor__section">{p.name || t('carte.sans_nom')}</h2>
      <dl className="g-facts">
        <div>
          <dt>{t('carte.type')}</dt>
          <dd>{p.track_label || '—'}</dd>
        </div>
        {p.activities.length > 0 && (
          <div>
            <dt>{t('carte.activites')}</dt>
            <dd>{p.activities.join(', ')}</dd>
          </div>
        )}
        {p.length_m != null && (
          <div>
            <dt>{t('carte.longueur')}</dt>
            <dd>{formatLength(p.length_m)}</dd>
          </div>
        )}
        {p.surface && (
          <div>
            <dt>{t('carte.revetement')}</dt>
            <dd>{p.surface}</dd>
          </div>
        )}
        {p.osm_id && (
          <div>
            <dt>OpenStreetMap</dt>
            <dd>
              <a className="g-link" href={`https://www.openstreetmap.org/way/${p.osm_id}`} target="_blank" rel="noreferrer">
                way {p.osm_id} <span className="mdi mdi-open-in-new" aria-hidden />
              </a>
            </dd>
          </div>
        )}
      </dl>

      <h3 className="g-editor__section">{t('carte.relations')}</h3>
      {p.linked ? (
        <div className="g-relation g-relation--on">
          <span className="mdi mdi-link-variant" aria-hidden />
          <span>{t('carte.associe_parc')}</span>
          <button type="button" className="btn btn--sm g-danger" disabled={busy} onClick={onUnlink}>
            {t('carte.retirer')}
          </button>
        </div>
      ) : (
        <div className="g-relation">
          <span className="mdi mdi-link-variant-off" aria-hidden />
          <span>{t('carte.non_associe')}</span>
          <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={onLink}>
            {t('carte.associer')}
          </button>
        </div>
      )}
      <p className="g-hint">{t('carte.aide_relation')}</p>
      {p.other_organizations && p.other_organizations.length > 0 && (
        <p className="g-hint">{t('carte.aussi_associe', { names: p.other_organizations.join(', ') })}</p>
      )}

      <h3 className="g-editor__section">{t('carte.copier_titre')}</h3>
      {cutting ? (
        <div className="g-editor__vertex">
          <span>{cutPoints === 0 ? t('carte.aide_decoupe_1') : t('carte.aide_decoupe_2')}</span>
          <button type="button" className="btn btn--sm" onClick={onCancelCut}>
            {t('commun.annuler')}
          </button>
        </div>
      ) : (
        <div className="g-editor__buttons">
          <button type="button" className="btn btn--sm" onClick={onStartCut}>
            <span className="mdi mdi-content-cut" aria-hidden /> {t('carte.copier_portion')}
          </button>
          <button type="button" className="btn btn--sm" onClick={onCopyWhole}>
            <span className="mdi mdi-content-copy" aria-hidden /> {t('carte.copier_entier')}
          </button>
        </div>
      )}
      <p className="g-hint">{t('carte.aide_copier')}</p>
    </div>
  )
}
